import articlesAPI from 'dashboard/api/helpCenter/articles';
import articleAttachmentsAPI from 'dashboard/api/helpCenter/articleAttachments';
import { uploadExternalImage, uploadFile } from 'dashboard/helper/uploadHelper';
import { throwErrorMessage } from 'dashboard/store/utils/api';
import camelcaseKeys from 'camelcase-keys';

import types from '../../mutation-types';

const normalizeArticlePayload = payload => {
  if (!payload) return payload;
  if (!Array.isArray(payload.attachments)) return payload;
  return {
    ...payload,
    attachments: payload.attachments.map(a => camelcaseKeys(a)),
  };
};

export const actions = {
  index: async (
    { commit },
    { pageNumber, portalSlug, locale, status, authorId, categorySlug }
  ) => {
    try {
      commit(types.SET_UI_FLAG, { isFetching: true });
      const { data } = await articlesAPI.getArticles({
        pageNumber,
        portalSlug,
        locale,
        status,
        authorId,
        categorySlug,
      });
      const payload = camelcaseKeys(data.payload);
      const meta = camelcaseKeys(data.meta);
      const articleIds = payload.map(article => article.id);
      commit(types.CLEAR_ARTICLES);
      commit(types.ADD_MANY_ARTICLES, payload);
      commit(types.SET_ARTICLES_META, meta);
      commit(types.ADD_MANY_ARTICLES_ID, articleIds);
      return articleIds;
    } catch (error) {
      return throwErrorMessage(error);
    } finally {
      commit(types.SET_UI_FLAG, { isFetching: false });
    }
  },

  create: async ({ commit, dispatch }, { portalSlug, ...articleObj }) => {
    commit(types.SET_UI_FLAG, { isCreating: true });
    try {
      const { data } = await articlesAPI.createArticle({
        portalSlug,
        articleObj,
      });
      const payload = normalizeArticlePayload(camelcaseKeys(data.payload));
      const { id: articleId } = payload;
      commit(types.ADD_ARTICLE, payload);
      commit(types.ADD_ARTICLE_ID, articleId);
      commit(types.ADD_ARTICLE_FLAG, articleId);
      dispatch('portals/updatePortal', portalSlug, { root: true });
      return articleId;
    } catch (error) {
      return throwErrorMessage(error);
    } finally {
      commit(types.SET_UI_FLAG, { isCreating: false });
    }
  },

  show: async ({ commit }, { id, portalSlug }) => {
    commit(types.SET_UI_FLAG, { isFetching: true });
    try {
      const { data } = await articlesAPI.getArticle({ id, portalSlug });
      const payload = normalizeArticlePayload(camelcaseKeys(data.payload));
      const { id: articleId } = payload;
      commit(types.ADD_ARTICLE, payload);
      commit(types.ADD_ARTICLE_ID, articleId);
    } finally {
      commit(types.SET_UI_FLAG, { isFetching: false });
    }
  },

  updateAsync: async ({ commit }, { portalSlug, articleId, ...articleObj }) => {
    commit(types.UPDATE_ARTICLE_FLAG, {
      uiFlags: { isUpdating: true },
      articleId,
    });

    try {
      await articlesAPI.updateArticle({ portalSlug, articleId, articleObj });
      return articleId;
    } catch (error) {
      return throwErrorMessage(error);
    } finally {
      commit(types.UPDATE_ARTICLE_FLAG, {
        uiFlags: { isUpdating: false },
        articleId,
      });
    }
  },

  update: async ({ commit }, { portalSlug, articleId, ...articleObj }) => {
    commit(types.UPDATE_ARTICLE_FLAG, {
      uiFlags: {
        isUpdating: true,
      },
      articleId,
    });

    try {
      const { data } = await articlesAPI.updateArticle({
        portalSlug,
        articleId,
        articleObj,
      });
      const payload = normalizeArticlePayload(camelcaseKeys(data.payload));
      commit(types.UPDATE_ARTICLE, payload);

      return articleId;
    } catch (error) {
      return throwErrorMessage(error);
    } finally {
      commit(types.UPDATE_ARTICLE_FLAG, {
        uiFlags: {
          isUpdating: false,
        },
        articleId,
      });
    }
  },

  promoteDraft: async ({ commit }, { portalSlug, articleId }) => {
    commit(types.UPDATE_ARTICLE_FLAG, {
      uiFlags: { isUpdating: true },
      articleId,
    });
    try {
      const { data } = await articlesAPI.promoteDraft({
        portalSlug,
        articleId,
      });
      const payload = normalizeArticlePayload(camelcaseKeys(data.payload));
      commit(types.UPDATE_ARTICLE, payload);
      return payload;
    } catch (error) {
      return throwErrorMessage(error);
    } finally {
      commit(types.UPDATE_ARTICLE_FLAG, {
        uiFlags: { isUpdating: false },
        articleId,
      });
    }
  },

  discardDraft: async ({ commit }, { portalSlug, articleId }) => {
    commit(types.UPDATE_ARTICLE_FLAG, {
      uiFlags: { isUpdating: true },
      articleId,
    });
    try {
      const { data } = await articlesAPI.discardDraft({
        portalSlug,
        articleId,
      });
      const payload = normalizeArticlePayload(camelcaseKeys(data.payload));
      commit(types.UPDATE_ARTICLE, payload);
      return payload;
    } catch (error) {
      return throwErrorMessage(error);
    } finally {
      commit(types.UPDATE_ARTICLE_FLAG, {
        uiFlags: { isUpdating: false },
        articleId,
      });
    }
  },

  updateArticleMeta: async ({ commit }, { portalSlug, locale }) => {
    try {
      const { data } = await articlesAPI.getArticles({
        pageNumber: 1,
        portalSlug,
        locale,
      });
      const meta = camelcaseKeys(data.meta);
      const { currentPage, ...metaWithoutCurrentPage } = meta;
      commit(types.SET_ARTICLES_META, metaWithoutCurrentPage);
    } catch (error) {
      throwErrorMessage(error);
    }
  },

  delete: async ({ commit }, { portalSlug, articleId }) => {
    commit(types.UPDATE_ARTICLE_FLAG, {
      uiFlags: {
        isDeleting: true,
      },
      articleId,
    });
    try {
      await articlesAPI.deleteArticle({ portalSlug, articleId });
      commit(types.REMOVE_ARTICLE, articleId);
      commit(types.REMOVE_ARTICLE_ID, articleId);
      return articleId;
    } catch (error) {
      return throwErrorMessage(error);
    } finally {
      commit(types.UPDATE_ARTICLE_FLAG, {
        uiFlags: {
          isDeleting: false,
        },
        articleId,
      });
    }
  },

  attachImage: async (_, { file }) => {
    const { fileUrl } = await uploadFile(file);
    return fileUrl;
  },

  uploadExternalImage: async (_, { url }) => {
    const { fileUrl } = await uploadExternalImage(url);
    return fileUrl;
  },

  fetchAttachments: async ({ commit }, { portalSlug, articleId }) => {
    try {
      const { data } = await articleAttachmentsAPI.list({
        portalSlug,
        articleId,
      });
      const attachments = (data || []).map(item => camelcaseKeys(item));
      commit(types.SET_ARTICLE_ATTACHMENTS, { articleId, attachments });
      return attachments;
    } catch (error) {
      return throwErrorMessage(error);
    }
  },

  uploadAttachment: async (
    { commit },
    { portalSlug, articleId, file, signal }
  ) => {
    const { data } = await articleAttachmentsAPI.upload({
      portalSlug,
      articleId,
      file,
      signal,
    });
    const attachment = camelcaseKeys(data);
    commit(types.ADD_ARTICLE_ATTACHMENT, { articleId, attachment });
    return attachment;
  },

  deleteAttachment: async (
    { commit },
    { portalSlug, articleId, attachmentId }
  ) => {
    try {
      await articleAttachmentsAPI.delete({
        portalSlug,
        articleId,
        attachmentId,
      });
      commit(types.REMOVE_ARTICLE_ATTACHMENT, { articleId, attachmentId });
      return attachmentId;
    } catch (error) {
      return throwErrorMessage(error);
    }
  },

  reorder: async (_, { portalSlug, categorySlug, reorderedGroup }) => {
    try {
      await articlesAPI.reorderArticles({
        portalSlug,
        reorderedGroup,
        categorySlug,
      });
    } catch (error) {
      throwErrorMessage(error);
    }

    return '';
  },

  // The version history page owns this data, so — like searchMutations — these two reads
  // return their payload straight to the caller instead of normalizing it into the store.
  fetchVersions: async (_, { portalSlug, articleId }) => {
    try {
      const { data } = await articlesAPI.getVersions({ portalSlug, articleId });
      return (data.payload || []).map(item =>
        camelcaseKeys(item, { deep: true })
      );
    } catch (error) {
      return throwErrorMessage(error);
    }
  },

  fetchVersion: async (_, { portalSlug, articleId, versionId }) => {
    try {
      const { data } = await articlesAPI.getVersion({
        portalSlug,
        articleId,
        versionId,
      });
      return camelcaseKeys(data.payload, { deep: true });
    } catch (error) {
      return throwErrorMessage(error);
    }
  },

  restoreVersion: async ({ commit }, { portalSlug, articleId, versionId }) => {
    commit(types.UPDATE_ARTICLE_FLAG, {
      uiFlags: { isUpdating: true },
      articleId,
    });
    try {
      const { data } = await articlesAPI.restoreVersion({
        portalSlug,
        articleId,
        versionId,
      });
      const payload = normalizeArticlePayload(camelcaseKeys(data.payload));
      commit(types.UPDATE_ARTICLE, payload);
      return payload;
    } catch (error) {
      return throwErrorMessage(error);
    } finally {
      commit(types.UPDATE_ARTICLE_FLAG, {
        uiFlags: { isUpdating: false },
        articleId,
      });
    }
  },

  searchMutations: async (_, { portalSlug, articleId, query }) => {
    try {
      const { data } = await articlesAPI.searchMutations({
        portalSlug,
        articleId,
        query,
      });
      return (data.payload || []).map(item => camelcaseKeys(item));
    } catch (error) {
      return throwErrorMessage(error);
    }
  },

  addMutations: async ({ commit }, { portalSlug, articleId, articleIds }) => {
    try {
      const { data } = await articlesAPI.addMutations({
        portalSlug,
        articleId,
        articleIds,
      });
      const payload = normalizeArticlePayload(camelcaseKeys(data.payload));
      commit(types.UPDATE_ARTICLE, payload);
      return payload;
    } catch (error) {
      return throwErrorMessage(error);
    }
  },

  removeMutation: async ({ commit }, { portalSlug, articleId, mutationId }) => {
    try {
      const { data } = await articlesAPI.removeMutation({
        portalSlug,
        articleId,
        mutationId,
      });
      const payload = normalizeArticlePayload(camelcaseKeys(data.payload));
      commit(types.UPDATE_ARTICLE, payload);
      return payload;
    } catch (error) {
      return throwErrorMessage(error);
    }
  },

  translateArticle: async (_, { portalSlug, articleId, locales }) => {
    try {
      await articlesAPI.translateArticle({ portalSlug, articleId, locales });
    } catch (error) {
      return throwErrorMessage(error);
    }
    return null;
  },
};
